import {
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Typography,
  Button,
  SxProps,
  Theme,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { FilterValue } from "../../../../models/Filters/FilterValue";
import { useTheme } from "@mui/material/styles";

type AppDropDownProps = {
  isMultiSelect: boolean;
  title: string;
  valuesList?: string[];
  value?: string | null;
  list?: FilterValue[];
  height?: string;
  listHeight?: string;
  fontSize?: string;
  onMultiSelectionChange?: (ids: string[], name: string) => void;
  onSingleSelectionChange?: (id: string, name: string) => void;
  clearSelectedValuesByUniqueId?: number;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  width?: string;
  borderColor?: string;
  backgroundColor?: string;
  borderRadius?: string;
  listPadding?: string;
  placeholderText?: string;
  showPlaceholder?: boolean;
  menuZIndex?: number;
  sx?: SxProps<Theme>;
  labelTop?: string;
  inlinePlaceholder?: boolean;
};

const AppDropDown = ({
  title,
  isMultiSelect,
  valuesList,
  value,
  list,
  height,
  listHeight,
  fontSize,
  width,
  onMultiSelectionChange,
  onSingleSelectionChange,
  clearSelectedValuesByUniqueId,
  disabled,
  required,
  id,
  borderColor = "",
  backgroundColor,
  borderRadius = "4px",
  listPadding,
  placeholderText = "Select an option",
  showPlaceholder = true,
  menuZIndex = 1300,
  sx,
  labelTop = "",
  inlinePlaceholder = false,
}: AppDropDownProps) => {
  const [selectedIds, setSelectedIds] = useState<string[]>(valuesList || []);
  const [selectedId, setSelectedId] = React.useState<any>(value || "");
  const [menuWidth, setMenuWidth] = useState<number>();
  const selectRef = useRef<HTMLDivElement | null>(null);
  const order_id = useRef(clearSelectedValuesByUniqueId);
  const theme = useTheme();

  const handleMultiSelectionChange = (event: SelectChangeEvent<any[]>) => {
    const selectedValues = event.target.value;
    const validSelectedValues = Array.isArray(selectedValues)
      ? selectedValues.filter(Boolean)
      : [];
    setSelectedIds(validSelectedValues);
    if (onMultiSelectionChange !== undefined)
      onMultiSelectionChange(validSelectedValues, id!);
  };

  const handleSelectAll = () => {
    if (!list || list.length === 0) return;

    const allIds = list.map((option) => option.id);
    setSelectedIds(allIds);
    if (onMultiSelectionChange !== undefined) {
      onMultiSelectionChange(allIds, id!);
    }
  };

  const handleClearAll = () => {
    setSelectedIds([]);
    if (onMultiSelectionChange !== undefined) {
      onMultiSelectionChange([], id!);
    }
  };

  useEffect(() => {
    if (order_id.current) {
      if (order_id.current !== clearSelectedValuesByUniqueId) {
        setSelectedIds([]);
        order_id.current = clearSelectedValuesByUniqueId;
      }
    } else {
      order_id.current = clearSelectedValuesByUniqueId;
    }
  }, [clearSelectedValuesByUniqueId]);

  useEffect(() => {
    setSelectedId(value);
  }, [value]);

  useEffect(() => {
    setSelectedIds(valuesList || []);
  }, [valuesList]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const selectElement = selectRef.current;
    if (!selectElement) return;

    const updateWidth = () => {
      const newWidth = selectElement.clientWidth;
      setMenuWidth((prev) => (prev === newWidth ? prev : newWidth));
    };

    updateWidth();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updateWidth);
      observer.observe(selectElement);
      return () => {
        observer.disconnect();
      };
    }

    window.addEventListener("resize", updateWidth);
    return () => {
      window.removeEventListener("resize", updateWidth);
    };
  }, [width, isMultiSelect]);

  const handleSingleSelectionChange = (event: SelectChangeEvent<any[]>) => {
    setSelectedId(event.target.value as string);
    if (onSingleSelectionChange !== undefined)
      onSingleSelectionChange(event.target.value as string, id!);
  };

  const selectedSingleId = selectedId ?? "";
  const hasMatchingSingleOption = Boolean(
    list?.some((option) => String(option.id) === String(selectedSingleId)),
  );
  const shouldShrinkSingleLabel =
    Boolean(selectedSingleId) || hasMatchingSingleOption;

  return (
    <Box>
      {isMultiSelect && (
        <FormControl
          fullWidth
          sx={{
            "& .MuiOutlinedInput-root": {
              borderColor: borderColor || "#ddd",
              backgroundColor: backgroundColor,
              borderRadius: borderRadius,
              height: height,
              width: width || "100%",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: borderColor || "#ddd",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#6455ED",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#6455ED",
              },
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: "#6455ED",
            },
          }}
        >
          <InputLabel sx={{ top: labelTop, fontSize: fontSize  }}>
            {required === true ? title + "*" : title}
          </InputLabel>
          <Select
            multiple
            ref={selectRef}
            value={selectedIds}
            input={
              <OutlinedInput
                label={required === true ? title + "*" : title}
                sx={{ height: "20px" }}
              />
            }
            onChange={handleMultiSelectionChange}
            displayEmpty
            renderValue={(selected) => {
              if (selected.length === 0) {
                if (showPlaceholder) {
                  return (
                    <Typography sx={{ fontSize: "0.8rem", color: "gray" }}>
                      {placeholderText}
                    </Typography>
                  );
                } else {
                  return null;
                }
              }

              return (
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    color: theme.palette.mode === "dark" ? "gray" : "#000",
                  }}
                >
                  {`${selected.length} selected`}
                </Typography>
              );
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: list && list.length <= 3 ? "auto" : 300,
                  overflowY: list && list.length <= 3 ? "visible" : "auto",
                  minWidth: "200px",
                  width: menuWidth ? `${menuWidth}px` : undefined,
                },
              },
              MenuListProps: {
                style: {
                  padding: 0,
                },
              },
            }}
            sx={{
              height: "36.8px",
              padding: "0px",
              width: width || "100%",
            }}
            disabled={disabled}
          >
            {/* Select All and Clear buttons */}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                justifyContent: "space-around",
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Button
                size="small"
                variant="text"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSelectAll();
                }}
                sx={{
                  fontSize: "0.8rem",
                  py: 0.25,
                  px: 0.75,
                  minWidth: "auto",
                  height: "24px",
                  color: theme.palette.primary.main,
                  "&:hover": {
                    backgroundColor: theme.palette.primary.light + "20",
                  },
                }}
              >
                Select All
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClearAll();
                }}
                sx={{
                  fontSize: "0.8rem",
                  py: 0.25,
                  px: 0.75,
                  minWidth: "auto",
                  height: "24px",
                  color: theme.palette.primary.main,
                  "&:hover": {
                    backgroundColor: theme.palette.primary.light + "20",
                  },
                }}
              >
                Clear
              </Button>
            </Box>
            {list?.map((option) => (
              <MenuItem
                key={option.id}
                value={option.id}
                sx={{
                  borderRadius: "0.20rem",
                  width: "100%",
                  height: list?.length > 3 ? "35px" : "45px",
                  "&.Mui-selected": {
                    backgroundColor: "transparent",
                  },
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}
              >
                <Checkbox
                  checked={selectedIds.indexOf(option.id) > -1}
                  sx={{
                    padding: "0.8rem",
                    "&.Mui-checked": {
                      color: "#6455ED",
                    },
                    paddingLeft: "0px",
                  }}
                  size="small"
                />
                <ListItemText
                  slotProps={{
                    primary: {
                      fontSize: fontSize,
                      paddingTop: listPadding,
                      paddingRight: "0px",
                    },
                  }}
                  primary={option.value}
                  sx={{ margin: 0 }}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {!isMultiSelect && (
        <FormControl
          fullWidth
          sx={{
            "& .MuiOutlinedInput-root": {
              borderColor: borderColor || "#ddd",
              backgroundColor: backgroundColor,
              borderRadius: borderRadius,
              height: height,
              width: width || "100%",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: borderColor || "#ddd",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#6455ED",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#6455ED",
              },
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: "#6455ED",
            },
            ...sx,
          }}
        >
          {!inlinePlaceholder ? (
            <InputLabel
              shrink={shouldShrinkSingleLabel}
              sx={{ top: labelTop, fontSize: fontSize }}
            >
              {required === true ? title + "*" : title}
            </InputLabel>
          ) : null}
          <Select
            sx={{
              height: height || "40px",
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                boxSizing: "border-box",
                minHeight: "unset !important",
                py: 0,
                px: 1.5,
                height: "100%",
              },
            }}
            ref={selectRef}
            value={selectedId}
            label={
              inlinePlaceholder
                ? undefined
                : required === true
                  ? title + "*"
                  : title
            }
            displayEmpty={inlinePlaceholder || !selectedId}
            onChange={handleSingleSelectionChange}
            renderValue={
              inlinePlaceholder
                ? (selected) => {
                    const selectedValue = String(selected ?? "");
                    if (!selectedValue) {
                      return showPlaceholder ? (
                        <Typography
                          sx={{
                            fontSize: fontSize || "0.8rem",
                            color: "gray",
                          }}
                        >
                          {placeholderText}
                        </Typography>
                      ) : null;
                    }

                    const selectedOption = list?.find(
                      (option) => option.id === selectedValue,
                    );
                    return (
                      <Typography
                        sx={{
                          fontSize: fontSize || "0.8rem",
                          color:
                            theme.palette.mode === "dark" ? "gray" : "#000",
                        }}
                      >
                        {selectedOption?.value || selectedValue}
                      </Typography>
                    );
                  }
                : undefined
            }
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 200,
                  overflowY: "auto",
                  zIndex: menuZIndex,
                  width: menuWidth ? `${menuWidth}px` : undefined,
                },
              },
              sx: {
                zIndex: menuZIndex,
              },
            }}
            disabled={disabled}
          >
            {list?.map((option) => (
              <MenuItem
                key={option.id}
                value={option.id}
                sx={{ height: listHeight || "30px" }}
              >
                <ListItemText
                  slotProps={{
                    primary: { fontSize: fontSize, paddingTop: listPadding },
                  }}
                  primary={option.value}
                  sx={{ margin: 0 }}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Box>
  );
};

export default React.memo(AppDropDown);
